// Plan tab — the member's working space.
// Renders six states (per spec §6): Action Required → Approved →
// Pending Review → Assessment In Progress → Awaiting Payment → Active.
// Multiple sections can render simultaneously (most-urgent first).
//
// Sources of state:
//   /consumer/subscriptions  → Active state
//   /consumer/consultations  → Pending / Action Required / Approved states
//   ApplicationTray (local)  → Awaiting Payment state (browse selections)
//   /consumer/assessment/current → Assessment In Progress (when backend ships it)
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { FullPageLoader } from "@/components/Loader";
import { apiGet, apiPost } from "@/lib/api";
import { useApplicationTray } from "@/lib/applicationTray";
import useMarketingPrograms from "@/lib/useMarketingPrograms";
import { C, colorSchemeForProgram } from "@/lib/brand";
import { isPendingRecommendation } from "@/lib/consultation";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "@/components/Toast";
import Activation from "@/pages/app/Activation";

export default function Plan() {
  const [subs, setSubs] = useState(null);
  const [consults, setConsults] = useState(null);
  const [inProgress, setInProgress] = useState(null);
  const tray = useApplicationTray();
  // Used to backfill program metadata (name, brand color, etc.) when
  // the BE's session.selectedPrograms is just a list of `{programId}`
  // — happens for older sessions or with shape drift. Without this
  // backfill the tray rehydration would silently drop sessions whose
  // programName field was missing.
  const marketingPrograms = useMarketingPrograms();

  const refresh = async () => {
    const [s, c, ip, d] = await Promise.all([
      apiGet("/consumer/subscriptions").catch(() => []),
      apiGet("/consumer/consultations").catch(() => []),
      // Same endpoint Home uses — the legacy `/consumer/assessment/current`
      // path silently 404s and leaves the Plan tab thinking the member has
      // nothing in flight, even when an assessment is paid + partway done.
      apiGet("/consumer/assessment/sessions/current").catch(() => null),
      // Dashboard is our source of truth for the member's current
      // consultation. When the member submits the assessment, the session
      // endpoint returns null and /consumer/consultations may lag before
      // echoing the new record — but /consumer/dashboard already surfaces
      // it as currentConsultation. Using it as a backfill means the Plan
      // tab shows "Pending review" the instant the member lands back on
      // it after submitting, with zero wait on eventual consistency.
      apiGet("/consumer/dashboard").catch(() => null),
    ]);
    setSubs(Array.isArray(s) ? s : []);
    // Merge in the dashboard's currentConsultation if the consultations
    // list doesn't include it yet. Dedupe by id.
    const consultsList = Array.isArray(c) ? [...c] : [];
    const dashConsult = d?.currentConsultation;
    if (dashConsult && !consultsList.some((cc) => (cc._id || cc.id) === (dashConsult._id || dashConsult.id))) {
      consultsList.push(dashConsult);
    }
    setConsults(consultsList);
    setInProgress(ip);

    // ---------------------------------------------------------------
    // Rehydrate the application tray from the server-side session.
    //
    // The tray lives in localStorage, so a member who selected programs
    // in Safari then signed in on Chrome would see an empty tray on
    // the new browser. Recover by mirroring the awaiting-payment
    // session's `selectedPrograms` back into the tray. Only fires when
    // the local tray is empty (we don't want to overwrite a member's
    // current in-progress selections on the same device).
    // ---------------------------------------------------------------
    if (
      tray.items.length === 0 &&
      ip &&
      ip.status === "awaiting_payment" &&
      Array.isArray(ip.selectedPrograms) &&
      ip.selectedPrograms.length > 0
    ) {
      // Build a lookup table off the marketing-programs payload so we
      // can backfill name / slug / brandColor when the session only
      // carries a programId. Keyed by both id and slug because the BE
      // shape has shifted between them.
      const byKey = {};
      (Array.isArray(marketingPrograms) ? marketingPrograms : []).forEach((mp) => {
        if (mp?.id) byKey[mp.id] = mp;
        if (mp?.programId) byKey[mp.programId] = mp;
        if (mp?.slug) byKey[mp.slug] = mp;
        if (mp?._id) byKey[mp._id] = mp;
      });

      ip.selectedPrograms.forEach((p) => {
        const idKey = p?.programId || p?.id || p?.slug || p?.programSlug;
        if (!idKey) return;
        const mp = byKey[idKey] || byKey[p?.slug] || byKey[p?.programSlug];
        const programId = idKey;
        const programSlug = p?.programSlug || p?.slug || mp?.slug || idKey;
        const programName = p?.programName || p?.name || mp?.name || mp?.heroName || mp?.title || "";
        // If we STILL don't have a name (no marketing payload, no BE
        // name) fall back to a humanized slug — better to surface a
        // tile than to silently drop the selection.
        const fallbackName = programName || humanizeSlug(programSlug);
        tray.addOrReplace({
          programId,
          programSlug,
          programName: fallbackName,
          brandColor: p?.brandColor || mp?.brandColor,
          tierId: p?.tierId,
          tierKey: p?.tierKey,
          tierName: p?.tierName || p?.tierLabel,
          monthlyPrice: p?.monthlyPrice ?? p?.priceMonthly ?? null,
        });
      });
    }
  };

  useEffect(() => { refresh(); }, []);

  // Bucket consultations by status
  const buckets = useMemo(() => groupConsultations(consults || []), [consults]);

  if (subs === null || consults === null) return <FullPageLoader />;

  const activeSubs = subs.filter((s) => ["active", "paused", "cancel_at_period_end", "payment_failed"].includes(s.status));

  // Hide tray items that already exist as a *live* consultation (i.e.
  // one that's currently surfaced in any bucket above). Previously we
  // built this set from EVERY consultation — including ones in a
  // terminal state (declined recommendation, cancelled, abandoned).
  // That hid the tray selection without rendering anything else, so
  // the page collapsed to the empty state even though the nav badge
  // was correctly showing the tray count. Fix: only filter against
  // consultations the page is actually rendering, and against the
  // active subscriptions list.
  const liveConsultations = [
    ...buckets.actionRequired,
    ...buckets.recommendations,
    ...buckets.approved,
    ...buckets.pending,
  ];
  const liveProgramIds = new Set([
    ...liveConsultations.map((c) => c.programId).filter(Boolean),
    ...activeSubs.map((s) => s.programId).filter(Boolean),
  ]);
  const traySelections = tray.items.filter(
    (it) => (it.kind || "program") === "program" && !liveProgramIds.has(it.programId),
  );
  const researchSelections = tray.items.filter((it) => it.kind === "research");

  const totalCount = (
    buckets.actionRequired.length +
    buckets.recommendations.length +
    buckets.approved.length +
    buckets.pending.length +
    (inProgress ? 1 : 0) +
    traySelections.length +
    researchSelections.length +
    activeSubs.length
  );

  if (totalCount === 0) return <EmptyState />;

  return (
    <div data-testid="plan-page" style={{ background: C.cream, paddingBottom: 100 }}>
      <PlanHero counts={{
        action: buckets.actionRequired.length,
        recommendations: buckets.recommendations.length,
        approved: buckets.approved.length,
        pending: buckets.pending.length,
        inProgress: inProgress ? 1 : 0,
        selected: traySelections.length,
        active: activeSubs.length,
      }} />

      <div style={{ position: "relative", padding: "32px 24px 0", background: C.cream }}>
        {/* PlanHero owns its own bottom-diagonal via clipPath; no TopWedge here
            (a second wedge would double-cut and create the chevron artefact). */}
        <div style={{ maxWidth: 980, margin: "0 auto", position: "relative", zIndex: 2, display: "flex", flexDirection: "column", gap: 40 }}>

          {buckets.recommendations.length > 0 && (
            <PlanSection
              eyebrow="★ PHYSICIAN'S RECOMMENDATION"
              subline="Review the side-by-side comparison and choose how you'd like to move forward."
              testId="plan-section-recommendations"
            >
              {buckets.recommendations.map((c) => (
                <RecommendationCard
                  key={c._id || c.id}
                  consultation={c}
                  onDecided={refresh}
                />
              ))}
            </PlanSection>
          )}

          {buckets.actionRequired.length > 0 && (
            <PlanSection
              eyebrow="★ ACTION REQUIRED"
              subline="Your physician has a question or recommendation."
              testId="plan-section-action"
            >
              {buckets.actionRequired.map((c) => <ActionRequiredCard key={c._id || c.id} consultation={c} />)}
            </PlanSection>
          )}

          {buckets.approved.length > 0 && (
            <PlanSection
              eyebrow="★ APPROVED"
              subline="Activate your subscription to begin. Your full protocol unlocks the moment payment clears."
              testId="plan-section-approved"
            >
              {buckets.approved.map((c) => (
                <Activation key={c._id || c.id} consultation={c} onActivated={refresh} />
              ))}
            </PlanSection>
          )}

          {buckets.pending.length > 0 && (
            <PlanSection
              eyebrow="★ PENDING REVIEW"
              subline="Your physician will respond within 24 hours."
              testId="plan-section-pending"
            >
              {buckets.pending.map((c) => <PendingCard key={c._id || c.id} consultation={c} />)}
            </PlanSection>
          )}

          {inProgress && (
            <PlanSection
              eyebrow="★ ASSESSMENT IN PROGRESS"
              subline="Picking up where you left off. We've saved everything."
              testId="plan-section-in-progress"
            >
              <InProgressCard session={inProgress} />
            </PlanSection>
          )}

          {traySelections.length > 0 && (
            <PlanSection
              eyebrow="★ READY WHEN YOU ARE"
              subline="Pay one review fee to apply for all selected programs."
              testId="plan-section-awaiting-payment"
            >
              {traySelections.map((it) => (
                <SelectedCard key={it.programId} item={it} onRemove={() => {
                  // Spec §6: "tapping triggers confirmation: 'Remove
                  // Weight Management from your plan? You can always add
                  // it back.'" — using browser confirm keeps the change
                  // minimal; brand-styled modal can land in a follow-up.
                  if (window.confirm(`Remove ${it.programName} from your plan? You can always add it back.`)) {
                    tray.remove(it.programId);
                  }
                }} />
              ))}
              <FeeSummary items={traySelections} />
            </PlanSection>
          )}

          {researchSelections.length > 0 && (
            <PlanSection
              eyebrow="★ RESEARCH COMPOUNDS"
              subline="Standalone compounds from the research catalogue. Checkout is direct — no review fee."
              testId="plan-section-research"
            >
              {researchSelections.map((it) => (
                <ResearchSelectedCard
                  key={`${it.sku}-${it.variant || "subscription"}`}
                  item={it}
                  onRemove={() => {
                    if (window.confirm(`Remove ${it.name} from your plan?`)) {
                      tray.removeResearch(it.sku, it.variant);
                    }
                  }}
                />
              ))}
              <ResearchCheckoutSummary items={researchSelections} />
            </PlanSection>
          )}

          {activeSubs.length > 0 && (
            <PlanSection
              eyebrow="★ ACTIVE"
              subline="Your protocol is running."
              testId="plan-section-active"
            >
              {activeSubs.map((s) => {
                // Try to surface the dosing protocol alongside the active
                // subscription card. Backend links the two via either
                // sub.consultationId or sub.consultation.id; if neither
                // is present we just render the subscription card alone.
                const linkedConsult = consults.find((c) => {
                  const cid = c._id || c.id;
                  return cid && (cid === s.consultationId || cid === s.consultation?.id);
                }) || null;
                return (
                  <div key={s._id || s.id} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <ActiveSubscriptionCard sub={s} />
                    {linkedConsult && <PrescribedProtocolCard consultation={linkedConsult} />}
                  </div>
                );
              })}
            </PlanSection>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- helpers ----------------------------------------------------------

function groupConsultations(consults) {
  const actionRequired = [];
  const recommendations = [];
  const approved = [];
  const pending = [];
  for (const c of consults) {
    // Physician's pending program/tier recommendation — highest-priority
    // section. Renders side-by-side decision card with Accept / Decline.
    // Once decided the field stays on the consultation but with a
    // status/decided marker, so we filter to *pending only* — the
    // consultation then falls through into pending/approved/etc.
    if (isPendingRecommendation(c.physicianRecommendation)) {
      recommendations.push(c);
      continue;
    }
    if (
      c.alternativeSuggestion ||
      c.physicianQuestion ||
      c.status === "action_required" ||
      c.status === "info_requested"
    ) {
      actionRequired.push(c);
    } else if (c.status === "approved") {
      approved.push(c);
    } else if (
      // Accept every "physician hasn't decided yet" status the backend
      // has shipped over time. The contract canonical values are
      // "submitted" and "in_review"; "pending" / "pending_review" /
      // "reviewing" are legacy aliases. Anything that hasn't moved to
      // approved / denied / action_required falls into pending.
      ["submitted", "in_review", "pending", "pending_review", "reviewing", "awaiting_review"].includes(c.status)
    ) {
      pending.push(c);
    }
  }
  return { actionRequired, recommendations, approved, pending };
}

// ----- presentational ---------------------------------------------------

function PlanHero({ counts }) {
  const totalSelected = counts.action + (counts.recommendations || 0) + counts.approved + counts.pending + counts.inProgress + counts.selected + counts.active;
  const summary = [];
  if (counts.recommendations) summary.push(`${counts.recommendations} physician recommendation${counts.recommendations === 1 ? "" : "s"}`);
  if (counts.action) summary.push(`${counts.action} need${counts.action === 1 ? "s" : ""} attention`);
  if (counts.approved) summary.push(`${counts.approved} approved`);
  if (counts.pending) summary.push(`${counts.pending} pending`);
  if (counts.inProgress) summary.push(`assessment in progress`);
  if (counts.selected) summary.push(`${counts.selected} awaiting payment`);
  if (counts.active) summary.push(`${counts.active} active`);

  return (
    <section
      style={{
        background: C.ink,
        color: C.cream,
        padding: "64px 24px 96px",
        // Section is clipped directly so the bottom edge IS the
        // diagonal — matches StatusHero / Browse / Me / PreSelectionHome
        // for a consistent editorial transition into cream below.
        clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 32px), 0 100%)",
      }}
    >
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <Eyebrow color={C.peach}>★ MY PLAN</Eyebrow>
        <Headline before="WHERE YOU" italic="are." color={C.cream} italicColor={C.peach} size={76} className="mt-4" />
        <p style={{ marginTop: 22, fontSize: 14, color: "rgba(250,246,240,0.72)", letterSpacing: "0.06em" }}>
          {totalSelected} {totalSelected === 1 ? "program" : "programs"} · {summary.join(" · ")}
        </p>
      </div>
    </section>
  );
}

function PlanSection({ eyebrow, subline, testId, children }) {
  return (
    <section data-testid={testId}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>{eyebrow}</div>
      {subline && (
        <p style={{ marginTop: 8, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 17, color: C.warmGray }}>
          {subline}
        </p>
      )}
      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        {children}
      </div>
    </section>
  );
}

function ActionRequiredCard({ consultation }) {
  const programName = consultation.programName || consultation.programSlug || "Your program";
  const tier = consultation.selectedProtocolTier || consultation.tierName;
  const physician = consultation.assignedPhysician?.lastName ? `Dr. ${consultation.assignedPhysician.lastName}` : "Your physician";
  const alt = consultation.alternativeSuggestion;
  const isHard = alt?.type === "hard";
  return (
    <div data-testid={`action-card-${consultation._id || consultation.id}`} style={{ background: C.peach, color: C.peachDark, padding: 24, borderLeft: `4px solid ${C.peachDark}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em", textTransform: "uppercase" }}>{programName}</div>
          {tier && <div style={{ marginTop: 4, fontSize: 13, color: C.peachDark }}>{tier} tier</div>}
        </div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", padding: "4px 10px", background: C.ink, color: C.peach }}>
          {alt ? (isHard ? "REQUIRES CHANGE" : "PHYSICIAN SUGGESTS") : "ACTION REQUIRED"}
        </span>
      </div>
      <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.55, color: C.peachDark, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
        {alt?.physicianRationale || alt?.physicianNotes || `${physician} has a follow-up question.`}
      </p>
      <div style={{ marginTop: 18 }}>
        <Link to={`/app/care-team/${consultation._id || consultation.id}`} data-testid={`action-respond-${consultation._id || consultation.id}`} style={{ textDecoration: "none" }}>
          <Button variant="primary" size="sm">VIEW & RESPOND →</Button>
        </Link>
      </div>
    </div>
  );
}

function RecommendationCard({ consultation, onDecided }) {
  const rec = consultation.physicianRecommendation;
  const consultationId = consultation._id || consultation.id;
  const [deciding, setDeciding] = useState(null); // "accept" | "decline" | null

  // Current plan info can arrive nested on the recommendation
  // (`rec.currentProgram`) or live on the consultation directly.
  // Prefer the nested snapshot since it's the authoritative "before"
  // the physician saw when they drafted the recommendation.
  const current = rec?.currentProgram || {
    programName: consultation.programName || consultation.programSlug,
    protocolTier: consultation.selectedProtocolTier || consultation.tierName,
    tierLabel: consultation.tierLabel,
    monthlyPrice: consultation.monthlyPrice,
  };

  const physicianName = rec?.recommendedByName
    || (consultation.assignedPhysician?.displayName)
    || "Your physician";
  const credentials = consultation.assignedPhysician?.credentials;
  const physicianLabel = credentials ? `${physicianName}, ${credentials}` : physicianName;

  const decide = async (kind) => {
    if (deciding) return;
    setDeciding(kind);
    try {
      await apiPost(`/consumer/consultations/${consultationId}/recommendation/${kind}`, {});
      toast(
        kind === "accept"
          ? "Recommendation accepted — your plan has been updated."
          : "Got it — sticking with your original plan.",
        "success",
      );
    } catch (err) {
      // 400 "already decided" — treat as success (card will disappear on
      // refresh). Anything else, surface the error.
      if (err?.status !== 400) {
        toast(err?.detail || "Couldn't record your choice. Try again.", "error");
        setDeciding(null);
        return;
      }
    }
    await onDecided?.();
    setDeciding(null);
  };

  return (
    <div
      data-testid={`recommendation-card-${consultationId}`}
      style={{ background: C.pure, border: `1.5px solid ${C.warmGray3}`, padding: 28 }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
        ★ {physicianName.toUpperCase()} RECOMMENDS
      </div>
      <Headline
        before="A different"
        italic="path."
        size={44}
        className="mt-2"
      />

      {/* Side-by-side — current on the left, recommended on the right.
          Stacks on mobile. */}
      <div
        className="md:!grid-cols-2"
        style={{ marginTop: 28, display: "grid", gridTemplateColumns: "1fr", gap: 16 }}
      >
        <ProgramMiniCard
          label="YOUR CURRENT SELECTION"
          program={current}
          slug={consultation.programSlug || current.programSlug}
          emphasis={false}
        />
        <ProgramMiniCard
          label="PHYSICIAN'S RECOMMENDATION"
          program={{
            programName: rec?.programName,
            tierLabel: rec?.tierLabel,
            protocolTier: rec?.protocolTier,
            monthlyPrice: rec?.monthlyPrice,
            compoundDescription: rec?.compoundDescription,
            tierBadge: rec?.tierBadge,
          }}
          slug={rec?.programSlug || rec?.slug || consultation.programSlug}
          emphasis
        />
      </div>

      {/* Physician's reason */}
      {rec?.reasonText && (
        <div
          style={{
            marginTop: 24,
            padding: "20px 22px",
            background: C.bone,
            borderLeft: `3px solid ${C.peachDeep}`,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
            {physicianLabel.toUpperCase()} RECOMMENDS THIS:
          </div>
          <p
            style={{
              marginTop: 10,
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              fontSize: 20,
              lineHeight: 1.5,
              color: C.ink,
            }}
          >
            "{rec.reasonText}"
          </p>
        </div>
      )}

      {/* Decision CTAs + open-chat deep-link */}
      <div style={{ marginTop: 26, display: "flex", flexDirection: "column", gap: 12 }}>
        <Button
          variant="primary"
          block
          onClick={() => decide("accept")}
          disabled={!!deciding}
          testId="recommendation-accept"
        >
          {deciding === "accept" ? "UPDATING…" : "ACCEPT RECOMMENDATION →"}
        </Button>
        <Button
          variant="secondary"
          block
          onClick={() => decide("decline")}
          disabled={!!deciding}
          testId="recommendation-decline"
        >
          {deciding === "decline" ? "SAVING…" : "KEEP ORIGINAL PLAN"}
        </Button>
        <Link
          to={`/app/care-team/${consultationId}`}
          data-testid="recommendation-open-chat"
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.22em",
            color: C.ink,
            textAlign: "center",
            textDecoration: "none",
            paddingTop: 6,
          }}
        >
          QUESTIONS? MESSAGE {physicianName.split(" ").slice(-1)[0].toUpperCase()} →
        </Link>
      </div>
    </div>
  );
}

function ProgramMiniCard({ label, program, slug, emphasis }) {
  const tier = program.tierLabel || program.protocolTier || "";
  const price = program.monthlyPrice;
  const badge = program.tierBadge;
  const badgeLabel = badge === "MOST_POPULAR" ? "MOST POPULAR" : badge ? String(badge).replace(/_/g, " ") : null;
  return (
    <div
      data-testid={`program-mini-${emphasis ? "recommended" : "current"}`}
      style={{
        padding: 22,
        background: emphasis ? C.cream : C.bone,
        border: emphasis ? `2px solid ${C.peachDeep}` : `1px solid ${C.warmGray3}`,
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: emphasis ? C.peachDeep : C.warmGray }}>
        {label}
      </div>
      {badgeLabel && (
        <div style={{
          marginTop: 10,
          display: "inline-block",
          padding: "3px 8px",
          background: C.ink,
          color: C.peach,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.18em",
          alignSelf: "flex-start",
        }}>★ {badgeLabel}</div>
      )}
      <div style={{ marginTop: 14, fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
        {program.programName || "—"}
      </div>
      {tier && (
        <div style={{ marginTop: 4, fontSize: 13, color: C.warmGray, letterSpacing: "0.04em" }}>
          {tier} tier
        </div>
      )}
      {price != null && (
        <div style={{ marginTop: 14, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
          {fmtMoney(price)}<span style={{ fontSize: 12, color: C.warmGray, fontWeight: 500 }}>/mo</span>
        </div>
      )}
      {program.compoundDescription && (
        <p style={{ marginTop: 12, fontSize: 13, color: C.warmGray, lineHeight: 1.5 }}>
          {program.compoundDescription}
        </p>
      )}
      {/* "More info" deep-link → opens the full program detail page where
          the member can read what's in the tier, see comparison vs. other
          tiers, expected timeline, etc. before making the decision. */}
      {slug && (
        <Link
          to={`/app/programs/${slug}`}
          data-testid={`program-mini-more-info-${emphasis ? "recommended" : "current"}`}
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: `1px solid ${emphasis ? "rgba(217,123,80,0.3)" : C.warmGray3}`,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.22em",
            color: C.ink,
            textDecoration: "none",
          }}
        >
          MORE INFO →
        </Link>
      )}
    </div>
  );
}

function PrescribedProtocolCard({ consultation }) {
  const instructions = consultation?.dosageInstructions || "";
  const note = consultation?.publicPhysicianNote || "";
  if (!instructions.trim() && !note.trim()) return null;

  const physician = consultation?.assignedPhysician;
  const physicianLabel = (() => {
    if (!physician) return null;
    const name = physician.displayName || [physician.firstName, physician.lastName].filter(Boolean).join(" ");
    if (!name) return null;
    return physician.credentials ? `${name}, ${physician.credentials}` : name;
  })();

  // Walk the dosage block line-by-line and group sequential `•` lines
  // into a single <ul> so spacing reads like a real list rather than a
  // wall of bullet glyphs. Header lines (no bullet, no special prefix)
  // become subheads. "Clinical notes:" becomes an italic paragraph.
  const blocks = [];
  const lines = instructions.split("\n");
  let bulletBuffer = [];
  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    blocks.push({ kind: "bullets", items: bulletBuffer.slice() });
    bulletBuffer = [];
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flushBullets();
      blocks.push({ kind: "spacer" });
      continue;
    }
    if (/^\s*•/.test(line)) {
      bulletBuffer.push(line.replace(/^\s*•\s*/, ""));
      continue;
    }
    flushBullets();
    if (/^clinical notes\s*:/i.test(line)) {
      blocks.push({ kind: "note", text: line });
    } else {
      blocks.push({ kind: "header", text: line });
    }
  }
  flushBullets();

  return (
    <div
      data-testid="prescribed-protocol-card"
      style={{
        background: C.pure,
        border: `1.5px solid ${C.warmGray3}`,
        padding: 28,
        position: "relative",
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
        ★ YOUR PRESCRIBED PROTOCOL
      </div>
      <Headline before="The" italic="prescription." size={36} className="mt-2" />

      {instructions.trim() && (
        <div data-testid="protocol-instructions" style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 0 }}>
          {blocks.map((b, i) => {
            if (b.kind === "spacer") {
              return <div key={i} style={{ height: 12 }} aria-hidden />;
            }
            if (b.kind === "header") {
              return (
                <div
                  key={i}
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: C.ink,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    marginTop: i === 0 ? 0 : 8,
                  }}
                >
                  {b.text}
                </div>
              );
            }
            if (b.kind === "bullets") {
              return (
                <ul
                  key={i}
                  style={{
                    margin: "10px 0 0",
                    padding: 0,
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {b.items.map((t, j) => (
                    <li
                      key={j}
                      style={{
                        position: "relative",
                        paddingLeft: 18,
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: C.ink,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          color: C.peachDeep,
                          fontWeight: 800,
                        }}
                      >
                        •
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              );
            }
            if (b.kind === "note") {
              return (
                <p
                  key={i}
                  style={{
                    marginTop: 16,
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: C.warmGray,
                    fontStyle: "italic",
                  }}
                >
                  {b.text}
                </p>
              );
            }
            return null;
          })}
        </div>
      )}

      {note.trim() && (
        <div
          data-testid="protocol-physician-note"
          style={{
            marginTop: instructions.trim() ? 26 : 22,
            padding: "20px 22px",
            background: C.bone,
            borderLeft: `3px solid ${C.peachDeep}`,
          }}
        >
          {physicianLabel && (
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
              {physicianLabel.toUpperCase()}
            </div>
          )}
          <p
            style={{
              marginTop: physicianLabel ? 10 : 0,
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              fontSize: 19,
              lineHeight: 1.5,
              color: C.ink,
            }}
          >
            "{note}"
          </p>
        </div>
      )}

      {physicianLabel && instructions.trim() && !note.trim() && (
        <div style={{ marginTop: 22, fontSize: 12, color: C.warmGray, letterSpacing: "0.04em" }}>
          — {physicianLabel}
        </div>
      )}
    </div>
  );
}

function PendingCard({ consultation }) {
  // Backend sometimes returns a single programName/tierName (legacy
  // single-program consultation) and sometimes selectedPrograms[]
  // (multi-program contract). Normalise to a list so we can render
  // every program the member is applying for.
  const fromArray = Array.isArray(consultation.selectedPrograms)
    ? consultation.selectedPrograms.map((p) =>
        typeof p === "string"
          ? { name: p, tierName: "" }
          : {
              name: p.name || p.programName || "",
              tierName: p.tierName || p.tierLabel || p.tier || "",
              monthlyPrice: p.monthlyPrice,
            },
      )
    : [];
  const fromLegacy = consultation.programName || consultation.programSlug
    ? [{
        name: consultation.programName || consultation.programSlug,
        tierName: consultation.selectedProtocolTier || consultation.tierName || consultation.tierLabel || "",
        // Legacy consultations carry these top-level. Newer ones use
        // selectedPrograms[] above. Either path lights up the same UI.
        monthlyPrice: consultation.monthlyPrice,
        compoundDescription: consultation.compoundDescription || consultation.tierDescription,
      }]
    : [];
  const programs = (fromArray.length > 0 ? fromArray : fromLegacy).filter((p) => p.name);

  const physician = consultation.assignedPhysician?.lastName
    ? `Dr. ${consultation.assignedPhysician.lastName}`
    : (consultation.assignedPhysician ? "your physician" : null);
  const submitted = consultation.submittedAt;
  return (
    <div data-testid={`pending-card-${consultation._id || consultation.id}`} style={{ background: C.pure, border: `1.5px solid ${C.warmGray3}`, padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep, marginBottom: 8 }}>
            APPLYING FOR
          </div>
          {programs.length > 0 ? (
            <div data-testid="pending-card-selections" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {programs.map((p, i) => (
                <div
                  key={i}
                  style={{
                    paddingBottom: 14,
                    borderBottom: i < programs.length - 1 ? `1px solid ${C.warmGray3}` : "none",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
                        {p.name}
                      </div>
                      {p.tierName && (
                        <div style={{ marginTop: 4, fontSize: 12, color: C.warmGray, letterSpacing: "0.04em" }}>
                          {p.tierName} tier
                        </div>
                      )}
                    </div>
                    {p.monthlyPrice != null && (
                      <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <span style={{ fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
                          {fmtMoney(p.monthlyPrice)}
                        </span>
                        <span style={{ fontSize: 11, color: C.warmGray, fontWeight: 600 }}>/mo</span>
                      </div>
                    )}
                  </div>
                  {p.compoundDescription && (
                    <p
                      style={{
                        marginTop: 10,
                        fontSize: 13,
                        lineHeight: 1.6,
                        color: C.warmGray,
                      }}
                    >
                      {p.compoundDescription}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
              Your program
            </div>
          )}
        </div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: C.peachDeep, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span className="animate-pulse-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: C.peachDeep, display: "inline-block" }} />
          PENDING REVIEW
        </span>
      </div>
      <div style={{ marginTop: 16, fontSize: 13, color: C.warmGray, lineHeight: 1.5 }}>
        {submitted && <>Submitted {fmtDate(submitted)} · </>}
        {physician ? `${physician} reviewing` : "Awaiting physician assignment"}
      </div>
    </div>
  );
}

function InProgressCard({ session }) {
  const sessionId = session._id || session.id;
  const completed = (session.modules || []).filter((m) => m.status === "completed").length;
  const total = (session.modules || []).length;
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // The session's selected programs ride alongside the assessment so the
  // Plan tab can show *what* the member is applying for. Different backend
  // versions have shipped slightly different shapes, so we accept a few.
  const selectedRaw = Array.isArray(session.selectedPrograms) ? session.selectedPrograms : [];
  const selectedPrograms = selectedRaw
    .map((p) => {
      if (typeof p === "string") return { name: "", tierName: "", slug: p };
      return {
        name: p.name || p.programName || "",
        tierName: p.tierName || p.tierLabel || p.tier || "",
        slug: p.slug || p.programSlug || "",
        monthlyPrice: p.monthlyPrice,
      };
    })
    .filter((p) => p.name || p.slug);

  // Paid-fee detection — accept all known signals so the badge lights up
  // regardless of which backend revision wrote the session document.
  const isPaid =
    session.paymentReceived === true ||
    !!session.feeReceipt ||
    session.paymentStatus === "paid" ||
    session.paymentStatus === "succeeded" ||
    session.feeStatus === "paid";

  // Single-glance journey state. Plan-tab users typically have a paid
  // fee + an in-progress assessment at this point; the rest of the
  // states (submitted, in_review, approved, active) live on their
  // dedicated cards above. So this card only needs two:
  const stateLabel = isPaid && progressPct < 100 ? "ASSESSMENT IN PROGRESS" : "ASSESSMENT NOT STARTED";

  return (
    <div data-testid="in-progress-card" style={{ background: C.bone, padding: 24, borderLeft: `4px solid ${C.peachDeep}` }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
        {isPaid ? "● PAYMENT RECEIVED" : "● AWAITING PAYMENT"}
      </div>
      <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" }}>
        YOUR ASSESSMENT
      </div>

      {selectedPrograms.length > 0 && (
        <div data-testid="in-progress-selections" style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {selectedPrograms.map((p, i) => (
            <div key={(p.slug || "") + i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, paddingBottom: 8, borderBottom: `1px solid ${C.warmGray3}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {p.name || p.slug}
              </div>
              <div style={{ fontSize: 12, color: C.warmGray, letterSpacing: "0.04em", textAlign: "right" }}>
                {p.tierName ? `${p.tierName} tier` : ""}
                {p.monthlyPrice ? ` · ${fmtMoney(p.monthlyPrice)}/mo` : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.warmGray }}>
        <span>● {stateLabel}</span>
        {total > 0 && <span>{completed} OF {total} MODULES</span>}
      </div>
      {total > 0 && (
        <div aria-hidden style={{ marginTop: 8, height: 4, background: C.warmGray3, position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, width: `${progressPct}%`, background: C.peachDeep }} />
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <Link to={`/app/assessment/${sessionId}`} data-testid="in-progress-continue" style={{ textDecoration: "none" }}>
          <Button variant="primary" size="sm">
            {progressPct === 0 ? "BEGIN ASSESSMENT →" : "CONTINUE ASSESSMENT →"}
          </Button>
        </Link>
      </div>
    </div>
  );
}

function SelectedCard({ item, onRemove }) {
  const scheme = colorSchemeForProgram({ slug: item.programSlug, color: item.brandColor });
  return (
    <div data-testid={`selected-card-${item.programSlug}`} style={{ background: scheme.background, color: scheme.textPrimary, padding: 24, position: "relative" }}>
      <button
        type="button"
        data-testid={`selected-remove-${item.programSlug}`}
        onClick={onRemove}
        aria-label={`Remove ${item.programName}`}
        style={{ position: "absolute", top: 12, right: 12, background: "transparent", border: 0, color: scheme.textPrimary, fontSize: 18, cursor: "pointer", padding: 4, lineHeight: 1 }}
      >
        ×
      </button>
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", textTransform: "uppercase", color: scheme.textPrimary, paddingRight: 24 }}>
        {item.programName}
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: scheme.textSecondary, letterSpacing: "0.05em" }}>
        {item.tierName ? `${item.tierName} tier` : ""}{item.monthlyPrice ? ` · ${fmtMoney(item.monthlyPrice)}/mo` : ""}
      </div>
      <div style={{ marginTop: 14, fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: scheme.textSecondary }}>
        ● AWAITING PAYMENT
      </div>
    </div>
  );
}

function FeeSummary({ items }) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  // Dollars (matches fmtMoney elsewhere in the codebase — tier monthlyPrice,
  // subscription monthlyPrice). The fine-print copy below ("$49 first
  // program") is the source of truth for the unit.
  const baseFee = 49;
  const additionalFee = 19;
  const fee = items.length === 0 ? 0 : baseFee + additionalFee * (items.length - 1);

  // Spec §3 Path B: from Plan's "Awaiting payment" state, tapping PAY &
  // BEGIN ASSESSMENT moves the member into payment + assessment. Sprint 4
  // will introduce a dedicated post-payment confirmation step; for now we
  // hand the tray off to /consumer/assessment/start (same surface as the
  // pre-selection home flow) and route to the returned session.
  const begin = async () => {
    if (items.length === 0 || starting) return;
    setStarting(true);
    try {
      const res = await apiPost("/consumer/assessment/start", {
        applicationType: "specific_programs",
        selectedProgramIds: items.map((i) => i.programId),
        selectedTiers: items.map((i) => ({
          programId: i.programId,
          tierId: i.tierId,
          tierKey: i.tierKey,
        })),
      });
      const id = res?._id || res?.id || res?.sessionId;
      // Lock the session to the choice-led path on the backend so the
      // assessment runner skips the discovery module and the fee logic
      // sticks to $49 + $19/additional. Silent fall-through if BE
      // hasn't shipped the endpoint yet — Checkout infers from
      // applicationType=specific_programs and tray items.
      if (id) {
        try {
          await apiPost(`/consumer/assessment/sessions/${id}/path-type`, {
            pathType: "choice_led",
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[VOYA9] path-type endpoint not available; Checkout will infer from applicationType.", e?.status);
        }
      }
      // Sprint 4 (spec §8): hand off to the Stripe checkout step before
      // the post-payment welcome screen. Backend creates the assessment
      // session in 'awaiting_payment' state; on success Checkout.jsx
      // navigates to /app/assessment/:id/welcome.
      navigate(id ? `/app/checkout/${id}` : "/app/assessment");
    } catch (err) {
      toast(err.detail || "Couldn't start your assessment. Try again in a moment.", "error");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div data-testid="plan-fee-summary" style={{ background: C.peach, color: C.peachDark, padding: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDark }}>★ TOTAL REVIEW FEE</div>
      <div style={{ marginTop: 10, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", color: C.ink }}>
        {fmtMoney(fee)}
      </div>
      <p style={{ marginTop: 8, fontSize: 13, color: C.peachDark, lineHeight: 1.55 }}>
        $49 first program{items.length > 1 ? ` + $19 × ${items.length - 1} additional = ${fmtMoney(fee)}` : ""}.
        This covers physician review for all selected programs. After approval, you activate subscriptions separately.
      </p>
      <div style={{ marginTop: 18 }}>
        <Button variant="primary" size="lg" block testId="plan-pay-begin-btn" onClick={begin} disabled={starting}>
          {starting ? "STARTING…" : "PAY & BEGIN ASSESSMENT →"}
        </Button>
      </div>
    </div>
  );
}

// Research compound row in the Plan tray. Visual treatment matches the
// research surface (navy + mono accent) so members can instantly tell
// these are different lane from the program-bound selections above.
function ResearchSelectedCard({ item, onRemove }) {
  const NAVY = "#1F3349";
  const NAVY_DEEP = "#0F1B2A";
  const variant = item.variant || "subscription";
  const unitCents = variant === "subscription" ? item.unitMonthlyPrice : item.unitOneTimePrice;
  const qty = item.quantity || 1;
  const lineCents = unitCents != null ? unitCents * qty : null;
  const variantLabel = variant === "subscription" ? "SUBSCRIBE" : "ONE-TIME";
  return (
    <div
      data-testid={`research-selected-card-${item.sku}`}
      style={{
        background: "#FFFFFF",
        color: NAVY_DEEP,
        padding: 24,
        position: "relative",
        border: `1px solid ${C.warmGray4}`,
        borderLeft: `4px solid ${NAVY}`,
      }}
    >
      <button
        type="button"
        data-testid={`research-selected-remove-${item.sku}`}
        onClick={onRemove}
        aria-label={`Remove ${item.name}`}
        style={{ position: "absolute", top: 12, right: 12, background: "transparent", border: 0, color: NAVY_DEEP, fontSize: 18, cursor: "pointer", padding: 4, lineHeight: 1 }}
      >
        ×
      </button>
      <div
        style={{
          fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
          fontSize: 10,
          letterSpacing: "0.25em",
          color: NAVY,
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        ▪ RESEARCH · {variantLabel}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", textTransform: "uppercase", color: NAVY_DEEP, paddingRight: 24 }}>
        {item.name}
      </div>
      {item.ingredient && (
        <div style={{ marginTop: 4, fontSize: 13, color: C.warmGray, fontStyle: "italic", fontFamily: '"Instrument Serif", Georgia, serif' }}>
          {item.ingredient}
        </div>
      )}
      <div style={{ marginTop: 14, display: "flex", gap: 18, alignItems: "baseline" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: NAVY_DEEP }}>
          {lineCents != null ? fmtMoney(lineCents / 100) : "—"}
        </div>
        <div style={{ fontSize: 11, letterSpacing: "0.18em", color: C.warmGray, textTransform: "uppercase" }}>
          {qty > 1 ? `${qty} × ${unitCents != null ? fmtMoney(unitCents / 100) : "—"}` : variant === "subscription" ? "/ month" : "/ vial"}
        </div>
      </div>
    </div>
  );
}

function ResearchCheckoutSummary({ items }) {
  const navigate = useNavigate();
  const NAVY = "#1F3349";
  const NAVY_DEEP = "#0F1B2A";
  const AMBER = "#E8C66B";
  // Ack is captured client-side and gates the CHECK OUT button. The
  // research-checkout page re-stamps the timestamp when it submits to
  // BE — we only need to know "did the user consent here?" before
  // letting them navigate.
  const [ackAt, setAckAt] = useState(null);
  const ackFinal = !!ackAt;
  const totalCents = items.reduce((sum, it) => {
    const unit = (it.variant || "subscription") === "subscription" ? it.unitMonthlyPrice : it.unitOneTimePrice;
    if (unit == null) return sum;
    return sum + unit * (it.quantity || 1);
  }, 0);
  const subCount = items.filter((it) => (it.variant || "subscription") === "subscription").length;
  const oneCount = items.filter((it) => it.variant === "one_time").length;
  const begin = () => {
    // Hand off to the dedicated VOYA9-branded research checkout page.
    // BE switched from hosted Stripe Checkout (redirect-to-Stripe) to
    // embedded Elements (client_secret), so we route the member into
    // /app/research/checkout where the Elements PaymentElement + ack
    // capture + payment confirmation all live. The new page reads the
    // tray.researchItems on mount, re-stamps `finalSaleAckAt`, and
    // POSTs to /consumer/research/checkout to fetch the clientSecret.
    if (items.length === 0 || !ackFinal) return;
    navigate("/app/research/checkout");
  };
  return (
    <div
      data-testid="research-checkout-summary"
      style={{ background: NAVY_DEEP, color: C.cream, padding: 24 }}
    >
      <div
        style={{
          fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
          fontSize: 10,
          letterSpacing: "0.3em",
          color: "rgba(250,246,240,0.65)",
          textTransform: "uppercase",
        }}
      >
        ▪ ORDER TOTAL
      </div>
      <div style={{ marginTop: 10, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", color: C.cream }}>
        {fmtMoney(totalCents / 100)}
      </div>
      <p style={{ marginTop: 8, fontSize: 13, color: "rgba(250,246,240,0.7)", lineHeight: 1.55 }}>
        {subCount > 0 && `${subCount} subscription${subCount === 1 ? "" : "s"}`}
        {subCount > 0 && oneCount > 0 ? " · " : ""}
        {oneCount > 0 && `${oneCount} one-time`}
        {". "}
        Subscriptions renew monthly until cancelled. One-time items ship once.
      </p>

      {/* Final-sale acknowledgement gate.
          The CHECK OUT button is disabled until the member explicitly
          ticks the box — gives legal a clear, dated record of consent
          and matches the prominence of the "ALL sales are FINAL"
          notice on /research. Amber accent ties back to the legal
          notice colour language. */}
      <label
        data-testid="research-checkout-ack-label"
        htmlFor="research-checkout-ack"
        style={{
          marginTop: 22,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "14px 16px",
          background: "rgba(232,198,107,0.08)",
          border: `1px solid ${ackFinal ? AMBER : "rgba(232,198,107,0.35)"}`,
          cursor: "pointer",
          transition: "border-color 160ms ease",
        }}
      >
        <input
          id="research-checkout-ack"
          data-testid="research-checkout-ack-checkbox"
          type="checkbox"
          checked={ackFinal}
          onChange={(e) => setAckAt(e.target.checked ? new Date().toISOString() : null)}
          style={{
            marginTop: 3,
            width: 16,
            height: 16,
            accentColor: AMBER,
            cursor: "pointer",
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, lineHeight: 1.55, color: C.cream }}>
          I understand these are research products and that{" "}
          <strong style={{ color: AMBER, fontWeight: 800 }}>all sales are final</strong>
          {" "}— no returns or refunds.
        </span>
      </label>

      <div style={{ marginTop: 18 }}>
        <Button
          variant="peach"
          size="lg"
          block
          testId="research-checkout-btn"
          onClick={begin}
          disabled={totalCents <= 0 || !ackFinal}
          style={{ background: NAVY === "" ? undefined : "#F4C9A8" }}
        >
          CHECK OUT →
        </Button>
        {!ackFinal && (
          <div
            data-testid="research-checkout-ack-helper"
            style={{
              marginTop: 10,
              fontSize: 11,
              letterSpacing: "0.05em",
              color: "rgba(250,246,240,0.55)",
              textAlign: "center",
            }}
          >
            Tick the box above to enable checkout.
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveSubscriptionCard({ sub }) {
  const scheme = colorSchemeForProgram({ slug: sub.programSlug, color: sub.brandColor });
  const subId = sub._id || sub.id;
  // Show the protocol link only when the backend has actually attached
  // an activeProtocol to the subscription. Falls back gracefully when
  // the field isn't present (legacy subs / pre-feature data).
  const hasProtocol =
    !!sub.activeProtocol ||
    !!sub.hasProtocol ||
    !!sub.protocolApprovedAt;
  return (
    <div data-testid={`active-sub-${subId}`} style={{ background: scheme.background, color: scheme.textPrimary, padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: scheme.textPrimary, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
            {sub.programName || sub.productName || "Your program"}
          </div>
          {sub.tierName && <div style={{ marginTop: 4, fontSize: 13, color: scheme.textSecondary }}>{sub.tierName} · {fmtMoney(sub.monthlyPrice)}/mo</div>}
        </div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", padding: "4px 10px", background: scheme.textPrimary, color: scheme.background }}>
          ✓ ACTIVE
        </span>
      </div>

      {hasProtocol && (
        <div
          data-testid={`active-protocol-summary-${subId}`}
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px solid ${scheme.textSecondary}33`,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: scheme.textPrimary }}>
            ★ YOUR PROTOCOL
          </div>
          {(sub.protocolDoseHeadline || sub.activeProtocol?.memberFinalized?.yourDose?.headline) && (
            <div style={{ marginTop: 8, fontSize: 14, color: scheme.textPrimary, lineHeight: 1.55 }}>
              {sub.protocolDoseHeadline || sub.activeProtocol.memberFinalized.yourDose.headline}
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <Link to={`/app/protocols/${subId}`} data-testid={`view-protocol-${subId}`} style={{ textDecoration: "none" }}>
              <Button variant="ghost" size="sm">VIEW FULL PROTOCOL →</Button>
            </Link>
          </div>
        </div>
      )}

      {sub.nextShipmentDate && (
        <div style={{ marginTop: 14, fontSize: 13, color: scheme.textSecondary }}>
          Next shipment {fmtDate(sub.nextShipmentDate)}{sub.nextCheckInDate ? ` · check-in ${fmtDate(sub.nextCheckInDate)}` : ""}
        </div>
      )}
      <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Link to="/app/plan/messages" data-testid={`active-message-${subId}`} style={{ textDecoration: "none" }}>
          <Button variant="ghost" size="sm">MESSAGE CARE TEAM</Button>
        </Link>
      </div>

      {/* Injection toolkit strip — every active member should have a one-tap
          path to their protocol card, logger, and history from this card.
          LOG is only surfaced when a protocol is on file; without one the
          BE returns 404 from /injection/log and we'd push the member into
          a dead end. */}
      <div
        data-testid={`active-injection-strip-${subId}`}
        style={{
          marginTop: 18,
          paddingTop: 14,
          borderTop: `1px dashed ${scheme.textSecondary}55`,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "center",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.18em",
        }}
      >
        <span style={{ color: scheme.textSecondary }}>★ INJECTION TOOLKIT</span>
        {hasProtocol && (
          <Link
            to="/app/injection/log"
            data-testid={`active-injection-log-${subId}`}
            style={{ color: scheme.textPrimary, textDecoration: "underline" }}
          >
            LOG →
          </Link>
        )}
        <Link
          to="/app/reference/injections"
          data-testid={`active-injection-reference-${subId}`}
          style={{ color: scheme.textPrimary, textDecoration: "underline" }}
        >
          PROTOCOL CARD →
        </Link>
        <Link
          to="/app/injection/history"
          data-testid={`active-injection-history-${subId}`}
          style={{ color: scheme.textPrimary, textDecoration: "underline" }}
        >
          HISTORY →
        </Link>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div data-testid="plan-empty" style={{ background: C.cream, paddingBottom: 80 }}>
      <section
        style={{
          background: C.ink,
          color: C.cream,
          padding: "72px 24px 96px",
          clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 32px), 0 100%)",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ MY PLAN</Eyebrow>
          <Headline before="NOTHING" italic="here yet." color={C.cream} italicColor={C.peach} size={76} className="mt-4" />
          <p style={{ marginTop: 22, fontSize: 16, color: "rgba(250,246,240,0.7)", lineHeight: 1.55, maxWidth: 540 }}>
            You haven't started a plan yet. Browse our programs and take the assessment to get matched with a physician.
          </p>
          <div style={{ marginTop: 32 }}>
            <Link to="/app/browse"><Button variant="peach" size="lg" testId="plan-empty-browse-btn">BROWSE PROGRAMS →</Button></Link>
          </div>
        </div>
      </section>
      <section style={{ padding: "32px 24px 52px", background: C.cream }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Eyebrow>WHILE YOU'RE HERE</Eyebrow>
          <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="!grid-cols-1 md:!grid-cols-2">
            <Link to="/app/refer" data-testid="plan-empty-refer-link" style={{ textDecoration: "none" }}>
              <div style={{ background: C.peach, color: C.peachDark, padding: "32px 28px" }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>★ REFER A FRIEND</div>
                <div style={{ marginTop: 12, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em" }}>Earn up to $30 per friend</div>
              </div>
            </Link>
            <Link to="/app/me" data-testid="plan-empty-me-link" style={{ textDecoration: "none" }}>
              <div style={{ background: C.bone, color: C.ink, padding: "32px 28px" }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>ACCOUNT</div>
                <div style={{ marginTop: 12, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em" }}>Update your profile</div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


// Last-resort program-name fallback used during tray rehydration when
// neither the session nor the marketing payload carried a name. Turns
// "weight-management" → "Weight Management" so the tile shows the
// member SOMETHING rather than silently disappearing.
function humanizeSlug(slug) {
  if (!slug || typeof slug !== "string") return "Selected program";
  return slug
    .split(/[-_/\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}
